import { useCallback, useState } from "react"
import {
  Button,
  CheckCircle,
  HeadingLarge,
  ParagraphLarge,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { useNavigate } from "@tanstack/react-router"
import { AnimatePresence, motion } from "framer-motion"

import { setOpenModal, useDappDispatch } from "#/redux-state"
import { routes } from "#/shared/constants"
import { useCopyValue, useSnackbarNotification } from "#/shared/hooks"
import { MarketItemDetailsLayout } from "#/ui/Market/MarketItemDetails/components"

import MarketTag from "../MarketTag"

export default function MarketRedeemCodeSuccess({
  type,
  transactionHash,
  redeemCode,
  image,
  successMessage,
  redeemButtonLabel,
  redeemLink,
}: {
  type: "store-product" | "gift-card"
  transactionHash: string
  redeemCode: string
  image: string
  successMessage: string
  redeemButtonLabel: string
  redeemLink: string
}) {
  const navigate = useNavigate()

  const [, theme] = useStyletron()
  const dispatch = useDappDispatch()

  const { copy } = useCopyValue(redeemCode, "Copy address")
  const { showSnackbarNotification } = useSnackbarNotification()

  const [addressCopiedForTheFirstTime, setAddressCopiedForTheFirstTime] =
    useState(false)

  const [
    shouldTransitionToFinalSuccessView,
    setShouldTransitionToFinalSuccessView,
  ] = useState(false)

  const redirectToFinalSuccessView = useCallback(() => {
    if (!addressCopiedForTheFirstTime) {
      setAddressCopiedForTheFirstTime(true)
      setTimeout(() => {
        setShouldTransitionToFinalSuccessView(true) // Trigger animation after 2 seconds
      }, 2000)
    }
  }, [addressCopiedForTheFirstTime])

  const copyRedeemCodeHandler = useCallback(() => {
    copy()
    showSnackbarNotification(
      "Redemption code copied!",
      theme.colors.black,
      () => <CheckCircle color={theme.colors.black} />,
    )
    redirectToFinalSuccessView()
  }, [
    copy,
    showSnackbarNotification,
    theme.colors.black,
    redirectToFinalSuccessView,
  ])

  const redeemNowHandler = useCallback(() => {
    window.open(redeemLink, "_blank")
    redirectToFinalSuccessView()
  }, [redeemLink, redirectToFinalSuccessView])

  const viewTransactionCallback = useCallback(async () => {
    dispatch(
      setOpenModal("transaction-details", {
        transactionHash,
      }),
    )
  }, [dispatch, transactionHash])

  return (
    <MarketItemDetailsLayout
      isSuccessPage
      image={image}
      overrides={{
        ContentContainer: {
          Block: { style: { alignItems: "center!important" } },
        },
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={shouldTransitionToFinalSuccessView ? "success" : "initial"}
          initial={
            shouldTransitionToFinalSuccessView ? { opacity: 0 } : { opacity: 1 }
          }
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MarketTag
            color="green"
            overrides={{
              Root: {
                style: {
                  marginBottom: theme.sizing.scale700,
                },
              },
            }}
          >
            {type === "store-product"
              ? "Bonus mats incoming!"
              : "Bonus 2x mats incoming!"}
          </MarketTag>

          <HeadingLarge marginBottom={theme.sizing.scale800}>
            Purchase Successful!
          </HeadingLarge>
          <ParagraphLarge marginBottom={theme.sizing.scale600}>
            {successMessage}
          </ParagraphLarge>
          {(shouldTransitionToFinalSuccessView || type === "gift-card") && (
            <Button
              kind="primary"
              overrides={{
                BaseButton: {
                  style: {
                    width: "100%",
                    marginBottom: theme.sizing.scale600,
                  },
                },
              }}
              onClick={redeemNowHandler}
            >
              {shouldTransitionToFinalSuccessView
                ? redeemButtonLabel
                : "Redeem now!"}
            </Button>
          )}
          {(shouldTransitionToFinalSuccessView || type === "store-product") && (
            <Button
              kind={
                shouldTransitionToFinalSuccessView ? "secondary" : "primary"
              }
              overrides={{
                BaseButton: {
                  style: {
                    width: "100%",
                    marginBottom: theme.sizing.scale600,
                  },
                },
              }}
              onClick={copyRedeemCodeHandler}
            >
              Copy Code: {redeemCode}
            </Button>
          )}
          {shouldTransitionToFinalSuccessView && (
            <>
              <Button
                kind="secondary"
                overrides={{
                  BaseButton: {
                    style: {
                      width: "100%",
                      marginBottom: theme.sizing.scale600,
                    },
                  },
                }}
                onClick={viewTransactionCallback}
              >
                View Transaction Detail
              </Button>

              <Button
                kind="tertiary"
                overrides={{
                  BaseButton: {
                    style: {
                      width: "100%",
                      marginBottom: theme.sizing.scale600,
                    },
                  },
                }}
                onClick={async () => {
                  await navigate({ to: `${routes.MARKET}` })
                }}
              >
                Back to Market
              </Button>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </MarketItemDetailsLayout>
  )
}
